import GeneralSettings from '@/components/Settings/components/GeneralSettings';
import ProfileSettings from '@/components/Settings/components/ProfileSettings';
import WorkspaceSettings from '@/components/Settings/components/WorkspaceSettings';
import IntegrationsSettings from '@/components/Settings/components/IntegrationsSettings';
import MembersSettings from '@/components/Settings/components/MembersSettings';
import BillingSettings from '@/components/Settings/components/BillingSettings';
import UsageSettings from '@/components/Settings/components/UsageSettings';
import SettingsWrapper from '@/components/Settings/SettingsWrapper';
import ValueLedgerPage from '@/components/ValueLedger/ValueLedgerPage';
import CloudAgentRunAuditPage from '@/components/AuditLog/CloudAgentRunAuditPage';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@client-app/i18n';
import React, {
    useCallback, useEffect, useMemo, useState
} from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

const SETTINGS_TABS = [
    'profile',
    'general',
    'workspace-settings',
    'integrations',
    'members',
    'billing',
    'usage',
    'value-ledger',
    'audit-log',
] as const;

type SettingsTab = (typeof SETTINGS_TABS)[number];

const SETTINGS_TAB_SET = new Set<string>(SETTINGS_TABS);

const isSettingsTab = (value?: string): value is SettingsTab => Boolean(value && SETTINGS_TAB_SET.has(value));

export const SettingsShellPage: React.FC = () => {
    const { t } = useTranslation();
    const { integrations } = useAuth();
    const { settingsTab } = useParams<{ settingsTab: string }>();
    const [searchParams] = useSearchParams();

    if (!isSettingsTab(settingsTab)) {
        return <Navigate to="/workspace-settings" replace />;
    }

    const activeTab = settingsTab;
    const connectProvider = searchParams.get('connect');
    const searchKey = searchParams.toString();
    const connectRequestId = useMemo(
        () => searchParams.get('connect_request_id') || (connectProvider ? `${Date.now()}` : null),
        [connectProvider, searchKey],
    );
    const [visitedTabs, setVisitedTabs] = useState<SettingsTab[]>(() => [activeTab]);
    const refreshIntegrations = integrations.refresh;
    const preloadIntegrations = useCallback(() => {
        void refreshIntegrations(false).catch(() => {});
    }, [refreshIntegrations]);
    const settingsTitles: Record<SettingsTab, string | undefined> = {
        general: t('website.settingsModal.general'),
        profile: t('sidebar.profile'),
        'workspace-settings': t('website.settingsModal.workspaceSettings'),
        integrations: undefined,
        members: t('website.settingsModal.users'),
        billing: t('website.settingsModal.billingAndPlans'),
        usage: t('website.settingsModal.usage'),
        'value-ledger': undefined,
        'audit-log': undefined,
    };

    useEffect(() => {
        setVisitedTabs((previous) => (
            previous.includes(activeTab) ? previous : [...previous, activeTab]
        ));
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'integrations') {
            preloadIntegrations();
        }
    }, [activeTab, preloadIntegrations]);

    return (
        <SettingsWrapper title={settingsTitles[activeTab]} scrollKey={activeTab}>
            {visitedTabs.includes('general') ? (
                <div hidden={activeTab !== 'general'} aria-hidden={activeTab !== 'general'}>
                    <GeneralSettings />
                </div>
            ) : null}

            {visitedTabs.includes('profile') ? (
                <div hidden={activeTab !== 'profile'} aria-hidden={activeTab !== 'profile'}>
                    <ProfileSettings />
                </div>
            ) : null}

            {visitedTabs.includes('workspace-settings') ? (
                <div
                    hidden={activeTab !== 'workspace-settings'}
                    aria-hidden={activeTab !== 'workspace-settings'}
                >
                    <WorkspaceSettings />
                </div>
            ) : null}

            {visitedTabs.includes('integrations') ? (
                <div hidden={activeTab !== 'integrations'} aria-hidden={activeTab !== 'integrations'}>
                    <IntegrationsSettings
                        workspaceIntegrations={integrations}
                        connectProvider={connectProvider}
                        connectRequestId={connectRequestId}
                    />
                </div>
            ) : null}

            {visitedTabs.includes('members') ? (
                <div hidden={activeTab !== 'members'} aria-hidden={activeTab !== 'members'}>
                    <MembersSettings />
                </div>
            ) : null}

            {visitedTabs.includes('billing') ? (
                <div hidden={activeTab !== 'billing'} aria-hidden={activeTab !== 'billing'}>
                    <BillingSettings onClose={() => {}} />
                </div>
            ) : null}

            {visitedTabs.includes('usage') ? (
                <div hidden={activeTab !== 'usage'} aria-hidden={activeTab !== 'usage'}>
                    <UsageSettings />
                </div>
            ) : null}

            {visitedTabs.includes('value-ledger') ? (
                <div hidden={activeTab !== 'value-ledger'} aria-hidden={activeTab !== 'value-ledger'}>
                    <ValueLedgerPage />
                </div>
            ) : null}

            {visitedTabs.includes('audit-log') ? (
                <div hidden={activeTab !== 'audit-log'} aria-hidden={activeTab !== 'audit-log'}>
                    <CloudAgentRunAuditPage />
                </div>
            ) : null}
        </SettingsWrapper>
    );
};
